import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  Box,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  IconButton,
} from "@mui/material";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { buildGraph } from "./dataTransform";
import GraphView from "./GraphView";
import Legend from "./Legend";
import MDTypography from "components/MDTypography";

function collectLocations(node, arr = [], depth = 0) {
  if (node.category === "Location" && node.children.length !== 0) {
    arr.push({ id: node.id, name: node.name, depth }); // 🟢 include depth
  }
  if (node.children) {
    node.children.forEach((c) => collectLocations(c, arr, depth + 1)); // 🔁 increase depth
  }
  return arr;
}

function findNodeById(node, id) {
  if (node.id === id) return node;
  if (node.children) {
    for (const c of node.children) {
      const found = findNodeById(c, id);
      if (found) return found;
    }
  }
  return null;
}

function countActiveInactiveChildren(node) {
  let active = 0,
    inactive = 0;
  if (node.children && node.children.length) {
    node.children.forEach((child) => {
      if (child.status === true && child.category !== "Location") active++;
      else if (child.status === false && child.category !== "Location") {
        inactive++;
      }
      const deeper = countActiveInactiveChildren(child);
      active += deeper.active;
      inactive += deeper.inactive;
    });
  }
  return { active, inactive };
}

export default function GraphContainer({ smartTopologyQuery }) {
  if (!smartTopologyQuery?.data) {
    return null;
  }

  const locationNodes = useMemo(() => {
    let arr = [];
    (smartTopologyQuery?.data || []).forEach((root) =>
      collectLocations(root, arr)
    );
    return arr;
  }, [smartTopologyQuery?.data]);

  const [selectedLocationId, setSelectedLocationId] = useState(() => {
    return localStorage.getItem("selectedLocationId") || "";
  });

  const [searchQuery, setSearchQuery] = useState("");

  const currentIndex = locationNodes.findIndex(
    (loc) => loc.id === selectedLocationId
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "ArrowRight" && currentIndex < locationNodes.length - 1) {
        setSelectedLocationId(locationNodes[currentIndex + 1].id);
      } else if (e.key === "ArrowLeft" && currentIndex > 0) {
        setSelectedLocationId(locationNodes[currentIndex - 1].id);
      }
    },
    [currentIndex, locationNodes]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    localStorage.setItem("selectedLocationId", selectedLocationId);
  }, [selectedLocationId]);

  const filteredData = useMemo(() => {
    if (!selectedLocationId) return smartTopologyQuery?.data;
    let selectedNode = null;
    for (const root of smartTopologyQuery?.data) {
      const found = findNodeById(root, selectedLocationId);
      if (found) {
        selectedNode = found;
        break;
      }
    }
    return selectedNode ? [selectedNode] : [];
  }, [selectedLocationId, smartTopologyQuery?.data]);

  const graphData = useMemo(() => buildGraph(filteredData), [filteredData]);

  return (
    <Box sx={{ flexGrow: 1, height: "80vh" }}>
      <Grid container spacing={0} sx={{ height: "100%", flexWrap: "nowrap" }}>
        <Grid
          item
          xs={2.5}
          sx={{
            borderRight: "1px solid #ccc",
            overflowY: "auto",
            height: "100%",
          }}
        >
          <MDTypography
            variant="h6"
            sx={{ mb: 1, fontWeight: "bold", textAlign: "center" }}
          >
            Locations List
          </MDTypography>
          <List dense>
            {locationNodes.map((loc) => {
              const node = findNodeById(
                { children: smartTopologyQuery?.data },
                loc.id
              );
              const { active, inactive } = node
                ? countActiveInactiveChildren(node)
                : { active: 0, inactive: 0 };

              // 🎨 Color based on depth
              let bgColor = "transparent";
              let textColor = "inherit";

              if (loc.depth === 0) {
                bgColor = "#4caf50"; // Green
                textColor = "black";
              } else if (loc.depth === 1) {
                bgColor = "#2196f3"; // Blue
                textColor = "black";
              } else {
                // bgColor = "rgba(0, 0, 0, 0.87)"; 
                 textColor = "white"; 
              }

              return (
                <ListItemButton
                  key={loc.id}
                  selected={selectedLocationId === loc.id}
                  onClick={() => setSelectedLocationId(loc.id)}
                  sx={{
                    backgroundColor: bgColor,
                    color: textColor,
                    "&.Mui-selected": {
                      backgroundColor: "#b2ad9d",
                      color: textColor,
                      "&:hover": {
                        backgroundColor: "#b2ad9d",
                      },
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <MDTypography variant="body2" sx={{ color: textColor }}>
                          {loc.name}
                        </MDTypography>
                        <Box sx={{ display: "flex", gap: 2 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                bgcolor: "green",
                              }}
                            />
                            <MDTypography variant="caption" sx={{ color: textColor }}>
                              {active}
                            </MDTypography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                bgcolor: "red",
                              }}
                            />
                            <MDTypography variant="caption" sx={{ color: textColor }}>
                              {inactive}
                            </MDTypography>
                          </Box>
                        </Box>
                      </Box>
                    }
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Grid>

        {/* Main Graph Area */}
        <Grid
          item
          xs={9.5}
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            position: "relative",
          }}
        >
          {/* Top Bar */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}
          >
            <Legend />
            <Box sx={{ position: "absolute", right: 0 }}>
              <TextField
                size="small"
                label="Search Assets"
                variant="outlined"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ backgroundColor: "white", borderRadius: 1, width: 250 }}
              />
            </Box>
          </Box>

          {/* Graph View */}
          <Box
            sx={{
              flexGrow: 1,
              position: "relative",
              overflow: "hidden",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <GraphView
              graphData={graphData}
              searchQuery={searchQuery}
              focusNodeId={selectedLocationId}
            />
          </Box>

          {/* Bottom Right Navigation */}
          <Box
            sx={{
              position: "absolute",
              bottom: 10,
              left: 10,
              display: "flex",
              gap: 1,
            }}
          >
            <IconButton
              onClick={() =>
                setSelectedLocationId(locationNodes[currentIndex - 1]?.id)
              }
              disabled={currentIndex <= 0}
            >
              <ArrowBack color="text" />
            </IconButton>
            <IconButton
              onClick={() =>
                setSelectedLocationId(locationNodes[currentIndex + 1]?.id)
              }
              disabled={currentIndex >= locationNodes.length - 1}
            >
              <ArrowForward color="text" />
            </IconButton>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
